import React, { useState, useEffect } from 'react';
import Modal from 'react-modal';
import { GoogleMap, Marker, TrafficLayer, Polyline } from '@react-google-maps/api';
import { PASSENGER_DETERMINED_DROP, isMissingDropoffAddressText } from './locationHelper';
import { getDirections } from '../../../services/directionsService';

const modalStyle = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '500px',
    padding: '15px',
    borderRadius: '8px',
    zIndex: 10,
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.3)'
  }
};

const mapContainerStyle = {
  width: '100%',
  height: '300px'
};

const WaypointModal = ({ isOpen, onClose, waypoint, allWaypoints, vehicleLocation, waypointIndex, fleetSettings }) => {
  const [mapCenter, setMapCenter] = useState(null);
  const [routeInfo, setRouteInfo] = useState({ previous: null, current: null, next: null });
  const [trafficData, setTrafficData] = useState({ duration: null, durationInTraffic: null });
  const [polylineSegments, setPolylineSegments] = useState([]);

  // Helper function to check if two locations are the same (within ~10 meters)
  const isSameLocation = (loc1, loc2) => {
    if (!loc1 || !loc2) return false;
    const lat1 = typeof loc1.lat === 'function' ? loc1.lat() : (loc1.lat || parseFloat(loc1[0]));
    const lng1 = typeof loc1.lng === 'function' ? loc1.lng() : (loc1.lng || parseFloat(loc1[1]));
    const lat2 = typeof loc2.lat === 'function' ? loc2.lat() : (loc2.lat || parseFloat(loc2[0]));
    const lng2 = typeof loc2.lng === 'function' ? loc2.lng() : (loc2.lng || parseFloat(loc2[1]));
    
    const latDiff = Math.abs(lat1 - lat2);
    const lngDiff = Math.abs(lng1 - lng2);
    return latDiff < 0.0001 && lngDiff < 0.0001;
  };

  // Helper function to get waypoint location as object
  const getLocationObject = (wp) => {
    if (!wp) return null;
    return {
      lat: parseFloat(wp.location[0]),
      lng: parseFloat(wp.location[1])
    };
  };

  // Find the nearest unique previous waypoint
  const findUniquePreviousWaypoint = (currentIndex) => {
    if (currentIndex <= 0) return null;
    
    const currentLocation = getLocationObject(allWaypoints[currentIndex]);
    
    for (let i = currentIndex - 1; i >= 0; i--) {
      const candidateWaypoint = allWaypoints[i];
      const candidateLocation = getLocationObject(candidateWaypoint);
      
      if (!isSameLocation(currentLocation, candidateLocation)) {
        return candidateWaypoint;
      }
    }
    return null;
  };

  // Find the nearest unique next waypoint
  const findUniqueNextWaypoint = (currentIndex) => {
    if (currentIndex >= allWaypoints.length - 1) return null;
    
    const currentLocation = getLocationObject(allWaypoints[currentIndex]);
    
    for (let i = currentIndex + 1; i < allWaypoints.length; i++) {
      const candidateWaypoint = allWaypoints[i];
      const candidateLocation = getLocationObject(candidateWaypoint);
      
      if (!isSameLocation(currentLocation, candidateLocation)) {
        return candidateWaypoint;
      }
    }
    return null;
  };

  useEffect(() => {
    if (!waypoint || !allWaypoints || !isOpen) return;

    // Find current waypoint index in allWaypoints
    const currentIndex = waypointIndex !== undefined ? waypointIndex : 
      allWaypoints.findIndex(wp => wp.jobNumber === waypoint.jobNumber && wp.type === waypoint.type);
    
    // Find unique waypoints, handling same-location cases
    const previousWaypoint = findUniquePreviousWaypoint(currentIndex);
    const nextWaypoint = findUniqueNextWaypoint(currentIndex);

    // Set map center to current waypoint
    const currentLocation = getLocationObject(waypoint);
    setMapCenter(currentLocation);

    // Build route: previous -> current -> next (or vehicle -> current -> next)
    const routePoints = [];
    
    // Add starting point (unique previous waypoint or vehicle location)
    if (previousWaypoint) {
      routePoints.push(getLocationObject(previousWaypoint));
    } else if (vehicleLocation && !isSameLocation(vehicleLocation, currentLocation)) {
      routePoints.push(vehicleLocation);
    }

    // Add current waypoint
    routePoints.push(currentLocation);

    // Add next waypoint if it exists and is unique
    if (nextWaypoint) {
      routePoints.push(getLocationObject(nextWaypoint));
    }

    // Store route info for display
    setRouteInfo({
      previous: previousWaypoint,
      current: waypoint,
      next: nextWaypoint,
      hasVehicleStart: !previousWaypoint && vehicleLocation && !isSameLocation(vehicleLocation, currentLocation)
    });

    // Request directions if we have at least 2 points. OSRM has no traffic data, so
    // duration_in_traffic mirrors the base duration (delay always 0).
    if (routePoints.length >= 2) {
      const osrmUrl =
        fleetSettings?.osrmUrl ||
        fleetSettings?.osrmBaseUrl ||
        fleetSettings?.OsrmBaseUrl ||
        null;

      // Issue one OSRM request per segment so we can color each leg independently.
      const legPromises = [];
      for (let i = 0; i < routePoints.length - 1; i++) {
        legPromises.push(
          getDirections({
            origin: routePoints[i],
            destination: routePoints[i + 1],
            osrmUrl,
          })
        );
      }

      Promise.all(legPromises)
        .then((legResults) => {
          const validLegs = legResults.filter(
            (r) => r && Array.isArray(r.path) && r.path.length > 0
          );
          if (validLegs.length === 0) {
            setPolylineSegments([]);
            setTrafficData({ duration: null, durationInTraffic: null });
            waypoint.etaDuration = undefined;
            waypoint.etaDistance = undefined;
            return;
          }

          const firstLeg = legResults[0];
          if (firstLeg) {
            const etaDurationMinutes = Math.round(firstLeg.duration / 60);
            const etaDistanceMiles = (firstLeg.distance * 0.000621371).toFixed(1);
            waypoint.etaDuration = etaDurationMinutes;
            waypoint.etaDistance = parseFloat(etaDistanceMiles);
            // OSRM has no traffic data — mirror duration to keep downstream UI happy.
            const durationObj = { value: firstLeg.duration };
            setTrafficData({
              duration: durationObj,
              durationInTraffic: durationObj,
            });
          }

          const segments = legResults.map((leg, legIndex) => {
            if (!leg || !leg.path || leg.path.length === 0) return null;
            let segmentColor;
            if (legIndex === 0) {
              segmentColor = routeInfo.hasVehicleStart ? '#4CAF50' : '#2196F3';
            } else {
              segmentColor = '#FF9800';
            }
            return {
              path: leg.path,
              color: segmentColor,
              strokeWeight: 4,
              strokeOpacity: 0.8,
            };
          }).filter(Boolean);

          setPolylineSegments(segments);
        })
        .catch(() => {
          setPolylineSegments([]);
          setTrafficData({ duration: null, durationInTraffic: null });
          waypoint.etaDuration = undefined;
          waypoint.etaDistance = undefined;
        });
    } else {
      // Clear ETA info if no route can be calculated
      waypoint.etaDuration = undefined;
      waypoint.etaDistance = undefined;
    }
  }, [waypoint, allWaypoints, vehicleLocation, waypointIndex, isOpen, fleetSettings]);

  if (!waypoint) return null;

  const formatTime = (timeString) => {
    if (!timeString) return '';
    try {
      // Handle different time formats
      let date;
      if (typeof timeString === 'number') {
        // Unix timestamp - check if it needs to be multiplied by 1000
        if (timeString < 10000000000) {
          // Timestamp is in seconds, convert to milliseconds
          date = new Date(timeString * 1000);
        } else {
          // Timestamp is already in milliseconds
          date = new Date(timeString);
        }
      } else if (typeof timeString === 'string') {
        // Check if it's already a time string like "13:23" or "01:23 PM"
        if (timeString.includes(':') && !timeString.includes(' ')) {
          // Format like "13:23" - just return as is
          return timeString;
        }
        
        // Handle AM/PM format like "2:40:00 PM" or "2:40 PM"
        if (timeString.includes(':') && (timeString.includes('AM') || timeString.includes('PM'))) {
          const today = new Date();
          const [timePart, meridiem] = timeString.split(' ');
          const timeParts = timePart.split(':');
          let hours = parseInt(timeParts[0]);
          const minutes = parseInt(timeParts[1]);
          const seconds = timeParts[2] ? parseInt(timeParts[2]) : 0;
          
          // Convert to 24-hour format
          if (meridiem === 'PM' && hours !== 12) {
            hours += 12;
          } else if (meridiem === 'AM' && hours === 12) {
            hours = 0;
          }
          
          date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds);
        } else {
          // Try parsing as date string
          date = new Date(timeString);
          
          // If invalid, try parsing as time only (assume today)
          if (isNaN(date.getTime()) && timeString.includes(':')) {
            const today = new Date();
            const timeParts = timeString.split(':');
            const hours = parseInt(timeParts[0]);
            const minutes = parseInt(timeParts[1]);
            date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes);
          }
        }
      } else {
        date = new Date(timeString);
      }
      
      if (isNaN(date.getTime())) {
        return timeString; // Return original if we can't parse it
      }
      
      // Return in 24-hour format
      return date.toLocaleTimeString([], { 
        hour: '2-digit', 
        minute: '2-digit',
        hour12: false // Force 24-hour format
      });
    } catch (error) {
      console.error('Error formatting time:', error);
      return timeString;
    }
  };

  const formatDuration = (minutes) => {
    if (!minutes && minutes !== 0) return '';
    return `${minutes} min`;
  };

  const formatDistance = (distance) => {
    if (!distance) return '';
    return `${distance} mi`;
  };

  const formatTrafficDuration = (durationObj) => {
    if (!durationObj) return '';
    return `${Math.round(durationObj.value / 60)} min`;
  };

  const calculateTrafficDelay = () => {
    if (!trafficData.duration || !trafficData.durationInTraffic) return null;
    const delay = (trafficData.durationInTraffic.value - trafficData.duration.value) / 60;
    return Math.round(delay);
  };

  const trafficDelay = calculateTrafficDelay();

  // Helper function to calculate travel duration and early/late status between waypoints
  const calculateTravelInfo = (fromWaypoint, toWaypoint, actualDurationMinutes = null) => {
    if (!fromWaypoint || !toWaypoint) return null;

    // Parse times to get timestamps
    const getTimestamp = (timeString) => {
      if (!timeString) return null;
      
      // Handle different time formats
      if (typeof timeString === 'number') {
        return timeString < 10000000000 ? timeString * 1000 : timeString;
      }
      
      if (typeof timeString === 'string') {
        // Handle AM/PM format like "2:40:00 PM"
        if (timeString.includes(':') && (timeString.includes('AM') || timeString.includes('PM'))) {
          const today = new Date();
          const [timePart, meridiem] = timeString.split(' ');
          const timeParts = timePart.split(':');
          let hours = parseInt(timeParts[0]);
          const minutes = parseInt(timeParts[1]);
          const seconds = timeParts[2] ? parseInt(timeParts[2]) : 0;
          
          if (meridiem === 'PM' && hours !== 12) {
            hours += 12;
          } else if (meridiem === 'AM' && hours === 12) {
            hours = 0;
          }
          
          const date = new Date(today.getFullYear(), today.getMonth(), today.getDate(), hours, minutes, seconds);
          return date.getTime();
        }
        
        // Try parsing as date
        const date = new Date(timeString);
        return !isNaN(date.getTime()) ? date.getTime() : null;
      }
      
      return null;
    };

    const fromTime = getTimestamp(fromWaypoint.timeStamp || fromWaypoint.time);
    const toTime = getTimestamp(toWaypoint.timeStamp || toWaypoint.time);

    if (!fromTime || !toTime) return null;

    // Calculate expected duration in minutes
    const expectedDurationMinutes = Math.round((toTime - fromTime) / (1000 * 60));
    
    if (expectedDurationMinutes <= 0) return null;

    // If we have actual travel time (from Google Maps), compare it
    if (actualDurationMinutes) {
      const difference = actualDurationMinutes - expectedDurationMinutes;
      const isLate = difference > 0;
      const diffText = Math.abs(difference) === 1 ? 'min' : 'mins';
      
      return {
        expectedDuration: expectedDurationMinutes,
        actualDuration: actualDurationMinutes,
        difference: Math.abs(difference),
        isLate,
        displayText: `${actualDurationMinutes} mins (${Math.abs(difference)} ${diffText} ${isLate ? 'late' : 'early'})`
      };
    }

    return {
      expectedDuration: expectedDurationMinutes,
      displayText: `${expectedDurationMinutes} mins`
    };
  };

  // Create waypoint sequence for tabular display
  const getWaypointSequence = () => {
    const sequence = [];
    
    // Only proceed if we have a current waypoint
    if (!routeInfo.current) {
      return sequence;
    }
    
    // Add previous waypoint or vehicle
    if (routeInfo.previous) {
      sequence.push({
        type: 'waypoint',
        data: routeInfo.previous,
        label: `${routeInfo.previous.type}:`,
        name: routeInfo.previous.paxName,
        time: formatTime(routeInfo.previous.time),
        color: '#9E9E9E',
        isVehicle: false
      });
    } else if (routeInfo.hasVehicleStart) {
      sequence.push({
        type: 'vehicle',
        data: {lat: vehicleLocation.lat, lng: vehicleLocation.lng},
        label: '',
        name: 'Vehicle Location',
        time: formatTime(new Date()),
        color: '#4CAF50',
        isVehicle: true
      });
    }
    
    // Add current waypoint
    sequence.push({
      type: 'current',
      data: routeInfo.current,
      label: `${routeInfo.current.type}:`,
      name: routeInfo.current.paxName,
      time: formatTime(routeInfo.current.time),
      color: '#2196F3',
      isVehicle: false
    });
    
    // Add next waypoint
    if (routeInfo.next) {
      sequence.push({
        type: 'waypoint',
        data: routeInfo.next,
        label: `${routeInfo.next.type}:`,
        name: routeInfo.next.paxName,
        time: formatTime(routeInfo.next.time),
        color: '#FF9800',
        isVehicle: false
      });
    }
    
    // Add travel duration info between waypoints
    const sequenceWithTravelInfo = [];
    for (let i = 0; i < sequence.length; i++) {
      sequenceWithTravelInfo.push(sequence[i]);
      
      // Add travel info between current and next waypoint
      if (i < sequence.length - 1) {
        const fromWaypoint = sequence[i];
        const toWaypoint = sequence[i + 1];
        
        // OSRM path used here does not expose Google Directions legs.
        // Keep null so calculateTravelInfo falls back to timestamp-based estimates.
        const actualDuration = null;
        
        const travelInfo = calculateTravelInfo(
          fromWaypoint.data || { time: new Date(), timeStamp: Date.now() }, 
          toWaypoint.data,
          actualDuration
        );
        
        if (travelInfo) {
          sequenceWithTravelInfo.push({
            type: 'travel',
            travelInfo,
            color: i === 0 ? (routeInfo.hasVehicleStart ? '#4CAF50' : '#2196F3') : '#FF9800'
          });
        }
      }
    }
    
    return sequenceWithTravelInfo;
  };

  const waypointSequence = getWaypointSequence();

  // Get pickup and dropoff info for current trip
  const getTripInfo = () => {
    const tripNumber = waypoint.jobNumber;
    const pickup = allWaypoints.find(wp => wp.jobNumber === tripNumber && wp.type === 'Pickup');
    const dropoff = allWaypoints.find(wp => wp.jobNumber === tripNumber && wp.type === 'Dropoff');
    
    return { pickup, dropoff };
  };

  const { pickup, dropoff } = getTripInfo();
  const dropoffDisplay1 =
    dropoff && isMissingDropoffAddressText(dropoff.address1)
      ? PASSENGER_DETERMINED_DROP
      : dropoff?.address1 ?? '';
  const dropoffShowLine2 =
    !!dropoff?.address2 &&
    !isMissingDropoffAddressText(dropoff.address2) &&
    dropoff.address2 !== dropoffDisplay1;

  return (
    <Modal
      isOpen={isOpen}
      onRequestClose={onClose}
      contentLabel="Waypoint Information"
      style={modalStyle}
    >
      <div className='flex flex-col h-full'>
        {/* Header */}
        <div className='flex justify-between items-center mb-3'>
          <h2 className='text-lg font-bold'>{waypoint.type}: {waypoint.paxName}</h2>
          <button 
            onClick={onClose} 
            className='text-gray-500 hover:text-gray-700 text-xl font-bold leading-none'
          >
            ×
          </button>
        </div>

        {/* Map Section */}
        {mapCenter && typeof window.google?.maps?.Map === 'function' && (
          <div className='mb-3 border border-gray-300 rounded-lg overflow-hidden'>
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={mapCenter}
              zoom={14}
              options={{
                disableDefaultUI: false,
                zoomControl: false,
                streetViewControl: false,
                mapTypeControl: false,
                gestureHandling: "greedy"
              }}
            >
              {/* Traffic Layer */}
              <TrafficLayer />

              {/* Colored polyline segments */}
              {polylineSegments.map((segment, index) => (
                <Polyline
                  key={index}
                  path={segment.path}
                  options={{
                    strokeColor: segment.color,
                    strokeOpacity: segment.strokeOpacity,
                    strokeWeight: segment.strokeWeight
                  }}
                />
              ))}

              {/* Custom markers for waypoints */}
              {routeInfo.hasVehicleStart && vehicleLocation && (
                <Marker
                  position={vehicleLocation}
                  icon={{
                    path: window.google?.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                    scale: 6,
                    fillColor: '#4CAF50',
                    fillOpacity: 1,
                    strokeColor: '#2E7D32',
                    strokeWeight: 2,
                    rotation: 0
                  }}
                  title="Vehicle Location"
                />
              )}

              {routeInfo.previous && (
                <Marker
                  position={getLocationObject(routeInfo.previous)}
                  icon={{
                    path: window.google?.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: '#9E9E9E',
                    fillOpacity: 1,
                    strokeColor: '#616161',
                    strokeWeight: 2
                  }}
                  title={`Previous: ${routeInfo.previous.type}`}
                />
              )}

              <Marker
                position={mapCenter}
                icon={{
                  path: window.google?.maps.SymbolPath.CIRCLE,
                  scale: 10,
                  fillColor: '#2196F3',
                  fillOpacity: 1,
                  strokeColor: '#1565C0',
                  strokeWeight: 3
                }}
                title={`Current: ${waypoint.type}`}
              />

              {routeInfo.next && (
                <Marker
                  position={getLocationObject(routeInfo.next)}
                  icon={{
                    path: window.google?.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: '#FF9800',
                    fillOpacity: 1,
                    strokeColor: '#F57C00',
                    strokeWeight: 2
                  }}
                  title={`Next: ${routeInfo.next.type}`}
                />
              )}
            </GoogleMap>
          </div>
        )}

        {/* Tabular Waypoint Display */}
        <div className='bg-gray-50 rounded-lg p-2 mb-3'>
          <div className='space-y-0'>
            {waypointSequence.map((item, index) => (
              <div key={index} style={{marginBottom:'-10px'}}>
                {/* Waypoint Row */}
                {item.type !== 'travel' && (
                  <div className='flex items-center'>
                    {/* Left side - Circle and connecting line */}
                    <div className='flex flex-col items-center mr-2' style={{ width: '20px' }}>
                      {/* Connecting line above (except for first item) */}
                      {index > 0 && !waypointSequence[index - 1]?.type === 'travel' && (
                        <div 
                          className='w-1 h-12'
                          style={{ 
                            backgroundColor: item.color
                          }}
                        />
                      )}
                      
                      {/* Circle */}
                      <div
                        className='w-4 h-4 rounded-full border-2'
                        style={{
                          backgroundColor: item.color,
                          borderColor: item.color,
                          transform: item.type === 'current' ? 'scale(1.2)' : 'scale(1)'
                        }}
                      />
                      
                      {/* Connecting line below (except for last item) */}
                      {index < waypointSequence.length - 1 && waypointSequence[index + 1]?.type !== 'travel' && (
                        <div 
                          className='w-1 h-12'
                          style={{ 
                            backgroundColor: item.color
                          }}
                        />
                      )}
                    </div>

                    {/* Right side - Waypoint information */}
                    <div className='flex-1'>
                      <div className='flex items-center justify-between py-0'>
                        <div className='flex-1'>
                          <div className='text-sm font-medium text-gray-800'>
                            {item.isVehicle ? (
                              <span>Vehicle Location</span>
                            ) : (
                              <span>{item.label} {item.name}</span>
                            )}
                          </div>
                          {!item.isVehicle && item.data && (
                            <div className='text-xs text-gray-600'>
                              {item.data.address1}
                              {item.data.wheelChair && <span className='ml-2 text-blue-600'>W/C</span>}
                            </div>
                          )}
                          {item.isVehicle && item.data && (
                            <div className='text-xs text-gray-600'>
                              {item.data.lat}, {item.data.lng}
                            </div>
                          )}
                        </div>
                        <div className='text-right'>
                          <div className='text-sm font-medium text-gray-800'>
                            {item.time}
                          </div>
                          {/* Show duration and distance for current waypoint */}
                          {item.type === 'current' && (
                            <div className='text-xs text-gray-600'>
                              {waypoint.etaDuration !== undefined && waypoint.etaDistance && (
                                <span>{formatDuration(waypoint.etaDuration)} - {formatDistance(waypoint.etaDistance)}</span>
                              )}
                              {trafficDelay > 0 && (
                                <div className='text-red-500'>+{trafficDelay} min traffic</div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {/* Travel Duration Row */}
                {item.type === 'travel' && item.travelInfo && (
                  <div className='flex items-center'>
                    {/* Left side - Connecting line only */}
                    <div className='flex flex-col items-center mr-2' style={{ width: '20px' }}>
                      <div 
                        className='w-1 h-12'
                        style={{ 
                          backgroundColor: item.color
                        }}
                      />
                    </div>

                    {/* Right side - Travel duration info */}
                    <div className='flex-1'>
                      <div className='flex items-center justify-center py-1'>
                        <div className={`text-xs px-2 py-1 rounded-full font-medium ${
                          item.travelInfo.isLate === true 
                            ? 'bg-red-100 text-red-700' 
                            : item.travelInfo.isLate === false 
                            ? 'bg-green-100 text-green-700'
                            : 'bg-gray-100 text-gray-700'
                        }`}>
                          <svg className="w-3 h-3 inline mr-1" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-12a1 1 0 10-2 0v4a1 1 0 00.293.707l2.828 2.829a1 1 0 101.415-1.415L11 9.586V6z" clipRule="evenodd" />
                          </svg>
                          {item.travelInfo.displayText}
                        </div>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Trip Details - Side by Side Pickup and Dropoff */}
        <div className='flex-1'>
          <div className='mb-2'>
            <h3 className='font-semibold text-gray-800 text-center'>Trip {waypoint.jobNumber}: {pickup.paxName}</h3>
          </div>

          <div className='grid grid-cols-2 gap-3'>
            {/* Pickup Information */}
            {pickup && (
              <div className='bg-yellow-50 p-3 rounded border-l-4 border-yellow-400'>
                <div className='flex items-center mb-1'>
                  <div className='w-3 h-3 rounded-full bg-yellow-400 mr-2'></div>
                  <h4 className='font-medium text-sm'>Pickup</h4>
                </div>
                <div className='space-y-0'>
                 
                  <div className='text-xs text-gray-700'>{pickup.address1}</div>
                  {pickup.address2 && pickup.address1!==pickup.address2 && (
                    <div className='text-xs text-gray-600'>{pickup.address2}</div>
                  )}
                  <div className='text-xs text-gray-600'>
                    {formatTime(pickup.time)}
                    {pickup.wheelChair && <span className='ml-2 text-blue-600'>W/C</span>}
                  </div>
                  {pickup.phone && (
                    <div className='text-xs text-blue-600'>{pickup.phone}</div>
                  )}
                </div>
              </div>
            )}

            {/* Dropoff Information */}
            {dropoff && (
              <div className='bg-orange-50 p-3 rounded border-l-4 border-orange-400'>
                <div className='flex items-center mb-1'>
                  <div className='w-3 h-3 rounded-full bg-orange-400 mr-2'></div>
                  <h4 className='font-medium text-sm'>Dropoff</h4>
                </div>
                <div className='space-y-0'>

                  <div className='text-xs text-gray-700'>{dropoffDisplay1}</div>
                  {dropoffShowLine2 && (
                    <div className='text-xs text-gray-600'>{dropoff.address2}</div>
                  )}
                  <div className='text-xs text-gray-600'>
                    {formatTime(dropoff.time)}
                    {dropoff.wheelChair && <span className='ml-2 text-blue-600'>W/C</span>}
                  </div>
                  {dropoff.phone && (
                    <div className='text-xs text-blue-600'>{dropoff.phone}</div>
                  )}
                </div>
              </div>
            )}
          </div>

          {/* Account Information */}
          {waypoint.account && (
            <div className='mt-3 text-center'>
              <div className='text-sm'>
                <span className='text-gray-600'>Account: </span>
                <span className='font-medium'>{waypoint.account}</span>
              </div>
            </div>
          )}
        </div>

        {/* Close Button */}
        <button 
          onClick={onClose} 
          className='mt-4 w-full bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded transition-colors'
        >
          Close
        </button>
      </div>
    </Modal>
  );
};

export default WaypointModal;
